import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Common } from 'src/app/dashboard/common';
import { FieldMapping } from 'src/app/dashboard/model/field-mapping';
import { SearchResultContainer } from 'src/app/dashboard/model/search-result-container';
import { AggregationResultContainer } from 'src/app/dashboard/old_model/aggregation-result-container';
import { AggregationRequestDto } from 'src/app/dashboard/model/aggregation-request-dto';
import { SearchRequestDto } from 'src/app/shared/search-request-dto';
import { SearchResultContainerDto } from 'src/app/shared/search-result-container-dto';
import { ActionResult, HelperFunctions } from 'bowman-commons';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, delay, map, tap } from 'rxjs/operators';
import { QueryCostEstimate } from './rate-limit/model/query-cost-estimate';
import { QueryCostEstimateBatchRequest } from './rate-limit/model/query-cost-estimate-batch';
import { RateLimitStateService } from './rate-limit/rate-limit-state.service';

@Injectable({
  providedIn: 'root'
})
export class QueryManagerService {

  private _mainUrl: string = window.location.pathname + '/api/query/';
  headers: HttpHeaders = new HttpHeaders();

  constructor(
    private _http: HttpClient,
    private _rateLimitState: RateLimitStateService
  ) {
    this.headers.set("Accept", "application/json");
  }

  get mainUrl() {
    return this._mainUrl;
  }

  public getFieldsMapping(): Observable<ActionResult<Array<FieldMapping>>> {
    let headers = new HttpHeaders();
    headers.set("Accept", "application/json");
    headers.set("Access-Control-Allow-Origin", "*");
    headers.set("Content-Type", "application/json");
    const dataUrl = HelperFunctions.cleanUrl(this.mainUrl + 'getFieldsMapping');
    return this._http.get<ActionResult<Array<FieldMapping>>>(dataUrl, { headers: headers }).pipe(catchError(this.handleError));
  }

  getPersistenceFilesOverview(): Observable<ActionResult<string>> {
    return null;
  }

  public search(request: SearchRequestDto): Observable<ActionResult<SearchResultContainer>> {
    let headers = new HttpHeaders();
    headers.set("Accept", "application/json");
    headers.set("Access-Control-Allow-Origin", "*");
    headers.set("Content-Type", "application/json");
    const dataUrl = HelperFunctions.cleanUrl(this.mainUrl + 'search');
    return this._http.post<ActionResult<SearchResultContainerDto>>(dataUrl, request, { headers: headers }).pipe(
      delay(1000),
      map((res: ActionResult<SearchResultContainerDto>) => {
        let ar: ActionResult<SearchResultContainer> = new ActionResult();
        ar.start = res.start;
        ar.status = res.status;
        ar.message = res.message;
        ar.duration = res.duration;
        ar.result = Common.unmarshallResponse(res.result);
        return ar;
      }),
      tap(response => this._rateLimitState.applyFromQueryResult(response.result)),
      catchError(this.handleError)
    );
  }

  public aggregate(request: SearchRequestDto): Observable<ActionResult<AggregationResultContainer>> {
    let headers = new HttpHeaders();
    headers.set("Accept", "application/json");
    headers.set("Access-Control-Allow-Origin", "*");
    headers.set("Content-Type", "application/json");
    const dataUrl = HelperFunctions.cleanUrl(this.mainUrl + 'aggregate');
    return this._http.post<ActionResult<AggregationResultContainer>>(dataUrl, request, { headers: headers }).pipe(
      map((res: ActionResult<AggregationResultContainer>) => {
        let ar: ActionResult<AggregationResultContainer> = new ActionResult();
        ar.start = res.start;
        ar.status = res.status;
        ar.message = res.message;
        ar.duration = res.duration;
        ar.result = res.result;
        return ar;
      }),
      tap(response => this._rateLimitState.applyFromQueryResult(response.result)),
      catchError(this.handleError)
    );
  }

  public estimateCosts(batch: QueryCostEstimateBatchRequest): Observable<Array<QueryCostEstimate>> {
    let headers = new HttpHeaders();
    headers.set('Accept', 'application/json');
    headers.set('Content-Type', 'application/json');
    const dataUrl = HelperFunctions.cleanUrl(this.mainUrl + 'estimateCosts');
    return this._http.post<ActionResult<Array<QueryCostEstimate>>>(dataUrl, batch, { headers: headers }).pipe(
      map(response => response.result || []),
      catchError(this.handleError)
    );
  }

  public estimateSearch(request: SearchRequestDto): Observable<QueryCostEstimate> {
    let headers = new HttpHeaders();
    headers.set("Accept", "application/json");
    headers.set("Content-Type", "application/json");
    const dataUrl = HelperFunctions.cleanUrl(this.mainUrl + 'estimateSearch');
    return this._http.post<ActionResult<QueryCostEstimate>>(dataUrl, request, { headers: headers }).pipe(
      map(response => response.result),
      catchError(this.handleError)
    );
  }

  public estimateAggregate(request: AggregationRequestDto): Observable<QueryCostEstimate> {
    let headers = new HttpHeaders();
    headers.set("Accept", "application/json");
    headers.set("Content-Type", "application/json");
    const dataUrl = HelperFunctions.cleanUrl(this.mainUrl + 'estimateAggregate');
    return this._http.post<ActionResult<QueryCostEstimate>>(dataUrl, request, { headers: headers }).pipe(
      map(response => response.result),
      catchError(this.handleError)
    );
  }

  public testTokenization(text: string): Observable<ActionResult<Array<string>>> {
    let headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
    const dataUrl = HelperFunctions.cleanUrl(this.mainUrl + 'testTokenization/standard');
    return this._http.post<ActionResult<Array<string>>>(dataUrl, '"' + text + '"', { headers: headers }).pipe(catchError(this.handleError));
  }

  private handleError(error: any) {
    let parsedError: any;
    if (error && error.error && error.error.message) {
      parsedError = error.error.message;
    } else if (error && error.message) {
      parsedError = error.message;
    } else if (error && error._body) {
      parsedError = error._body;
    } else {
      parsedError = 'Query Manager Service Error';
    }
    return observableThrowError(parsedError);
  }

}
